import { formatDateLocale, formatDateTimeLocale } from '@/common/utils/date';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BloqueadaBadge } from '@/components/BloqueadaBadge';
import TramiteBudgetIndicators from '@/components/TramiteBudgetIndicators';
import { ArchivadoBadge } from '@/components/ArchivadoBadge';
import { getVencimientoPresentacion } from '@/hooks/useVencimientoPresentacion';
import { useTramiteStatusSummary } from '@/components/TramiteStatusSummary/useTramiteStatusSummary';
import { TareasProgressCell } from '@/tramite-tareas';
import {
  Archive,
  ArchiveRestore,
  CalendarDays,
  ChevronRight,
  Clock3,
  Lock,
  LockOpen,
  MapPin,
  UserRound,
  UsersRound,
} from 'lucide-react';
import type {
  EscrituraKanbanStatus,
  ParticipantGroup,
  TramiteKanbanItem,
} from './types';

interface TramiteKanbanCardProps {
  tramite: TramiteKanbanItem;
  status: EscrituraKanbanStatus;
  canViewStatus: boolean;
  canViewTareas: boolean;
  canViewIndicators?: boolean;
  canEditAssignedUser: boolean;
  canBloquear?: boolean;
  canArchivar?: boolean;
  onViewTramite: (tramite: TramiteKanbanItem) => void;
  onViewParticipants: (
    tramite: TramiteKanbanItem,
    group: ParticipantGroup,
  ) => void;
  onViewStatus: (tramite: TramiteKanbanItem) => void;
  onViewTareas: (tramite: TramiteKanbanItem) => void;
  onEditAssignedUser: (tramite: TramiteKanbanItem) => void;
  onClickBloqueo?: (tramite: TramiteKanbanItem) => void;
  onClickArchivado?: (tramite: TramiteKanbanItem) => void;
}

const participantSummary = (
  participants: TramiteKanbanItem['vendedores'],
  emptyLabel: string,
) => {
  if (!participants?.length) {
    return emptyLabel;
  }

  const firstName = participants[0]?.nombre || 'Sin nombre';
  return participants.length > 1
    ? `${firstName} +${participants.length - 1}`
    : firstName;
};

const getRelevantDate = (
  tramite: TramiteKanbanItem,
  status: EscrituraKanbanStatus,
) => {
  if (status === 'sin_firmar') {
    return {
      label: 'Firma estimada',
      value: tramite.fecfirma
        ? formatDateTimeLocale(tramite.fecfirma)
        : 'Sin fecha',
    };
  }

  if (status === 'firmada_sin_presentar') {
    return {
      label: 'Fecha de escritura',
      value: tramite.fecescritura
        ? formatDateLocale(tramite.fecescritura)
        : 'Sin fecha',
    };
  }

  if (status === 'bloqueada') {
    return {
      label: 'Bloqueada el',
      value: tramite.bloqueado_at
        ? formatDateTimeLocale(tramite.bloqueado_at)
        : 'Sin fecha',
    };
  }

  return {
    label: 'Fecha de entrada',
    value: tramite.fecentrada
      ? formatDateLocale(tramite.fecentrada)
      : 'Sin fecha',
  };
};

export const TramiteKanbanCard = ({
  tramite,
  status,
  canViewStatus,
  canViewTareas,
  canViewIndicators,
  canEditAssignedUser,
  canBloquear,
  canArchivar,
  onViewTramite,
  onViewParticipants,
  onViewStatus,
  onViewTareas,
  onEditAssignedUser,
  onClickBloqueo,
  onClickArchivado,
}: TramiteKanbanCardProps) => {
  const { completed, totalEnabled, progressPercentage } =
    useTramiteStatusSummary(tramite);
  const relevantDate = getRelevantDate(tramite, status);
  const { vencimientoPresentacion, diasEscritura } =
    getVencimientoPresentacion(tramite);
  const assignedUsers = tramite.assigned_users ?? [];
  const assignedLabel =
    assignedUsers.length > 0
      ? `${assignedUsers[0].name}${
          assignedUsers.length > 1 ? ` +${assignedUsers.length - 1}` : ''
        }`
      : 'Sin responsable';

  return (
    <article className="rounded-xl border bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
      <header className="flex items-start justify-between gap-2">
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onViewTramite(tramite)}
          aria-label={`Ver trámite ${tramite.id}`}
        >
          #{tramite.id}
          <ChevronRight className="size-3.5" />
        </button>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          <BloqueadaBadge tramite={tramite} />
          <ArchivadoBadge tramite={tramite} />
          {canBloquear && onClickBloqueo && (
            tramite.bloqueado ? (
              // La escritura ya muestra el badge rojo "Bloqueada": alcanza con
              // un ícono para la acción de desbloquear, sin ambigüedad de estado.
              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onClickBloqueo(tramite)}
                title="Desbloquear escritura"
                aria-label={`Desbloquear escritura ${tramite.id}`}
              >
                <LockOpen className="size-3.5" />
              </button>
            ) : (
              // Sin texto, un candado cerrado se lee como "ya está bloqueada"
              // en vez de la acción de bloquear: se etiqueta explícitamente.
              <Badge
                variant="outline"
                className="cursor-pointer px-1.5 py-0.5 text-[10px] hover:opacity-80"
                onClick={() => onClickBloqueo(tramite)}
                aria-label={`Bloquear escritura ${tramite.id}`}
              >
                <Lock className="size-3 mr-0.5" /> Bloquear
              </Badge>
            )
          )}
          {canArchivar && onClickArchivado && (
            tramite.archivado ? (
              // Ya se ve el badge "Archivada": alcanza con el ícono de la acción.
              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onClickArchivado(tramite)}
                title="Desarchivar escritura"
                aria-label={`Desarchivar escritura ${tramite.id}`}
              >
                <ArchiveRestore className="size-3.5" />
              </button>
            ) : (
              <Badge
                variant="outline"
                className="cursor-pointer px-1.5 py-0.5 text-[10px] hover:opacity-80"
                onClick={() => onClickArchivado(tramite)}
                aria-label={`Archivar escritura ${tramite.id}`}
              >
                <Archive className="size-3 mr-0.5" /> Archivar
              </Badge>
            )
          )}
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            Esc. {tramite.num_escritura || 'S/N'}
          </span>
        </div>
      </header>

      <div className="mt-3 flex items-start gap-2">
        <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <p
          className="line-clamp-2 text-sm font-medium text-foreground"
          title={tramite.propiedad || 'Sin propiedad informada'}
        >
          {tramite.propiedad || 'Sin propiedad informada'}
        </p>
      </div>

      <div className="mt-3 space-y-2 border-t pt-3">
        <button
          type="button"
          className="flex w-full items-start gap-2 rounded-md text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onViewParticipants(tramite, 'vendedores')}
        >
          <UserRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
              Transferentes
            </span>
            <span className="block truncate text-xs font-medium">
              {participantSummary(tramite.vendedores, 'Sin transferentes')}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="flex w-full items-start gap-2 rounded-md text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onViewParticipants(tramite, 'compradores')}
        >
          <UsersRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
              Adquirientes
            </span>
            <span className="block truncate text-xs font-medium">
              {participantSummary(tramite.compradores, 'Sin adquirientes')}
            </span>
          </span>
        </button>
      </div>

      <div className="mt-3 grid gap-2 border-t pt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <CalendarDays className="size-3.5 shrink-0" />
          <span className="min-w-0 break-words">
            {relevantDate.label}: <strong>{relevantDate.value}</strong>
          </span>
        </div>
        {status === 'bloqueada' && tramite.bloqueado_motivo && (
          // En esta columna el motivo es el dato que explica por qué la
          // escritura está frenada; en el resto alcanza con el tooltip del badge.
          <div className="flex items-start gap-2 text-red-700">
            <Lock className="mt-0.5 size-3.5 shrink-0" />
            <span className="line-clamp-2" title={tramite.bloqueado_motivo}>
              {tramite.bloqueado_motivo}
            </span>
          </div>
        )}
        {status === 'firmada_sin_presentar' && (
          <div
            className={`flex items-center gap-2 font-medium ${
              diasEscritura <= 0 ? 'text-red-600' : 'text-orange-600'
            }`}
          >
            <Clock3 className="size-3.5 shrink-0" />
            <span className="min-w-0 break-words">Presentación: {vencimientoPresentacion}</span>
          </div>
        )}
        {canEditAssignedUser ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onEditAssignedUser(tramite)}
            aria-label={`Editar responsable del trámite ${tramite.id}`}
          >
            <UserRound className="size-3.5 shrink-0" />
            <span className="truncate" title={assignedLabel}>
              Responsable: <strong>{assignedLabel}</strong>
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <UserRound className="size-3.5 shrink-0" />
            <span className="truncate" title={assignedLabel}>
              Responsable: <strong>{assignedLabel}</strong>
            </span>
          </div>
        )}
      </div>

      {canViewStatus && (
        <button
          type="button"
          className="mt-3 w-full rounded-md border-t pt-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onViewStatus(tramite)}
          aria-label={`Ver estado de subtrámites del trámite ${tramite.id}`}
        >
          <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Progreso de subtrámites</span>
            <span className="font-mono font-medium">
              {completed}/{totalEnabled}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-1.5" />
        </button>
      )}

      {canViewTareas && (
        <div className="mt-3 border-t pt-3">
          <div className="mb-1.5 text-[11px] text-muted-foreground">Progreso de tareas</div>
          <TareasProgressCell tramite={tramite} clickable onClick={onViewTareas} />
        </div>
      )}

      {canViewIndicators && (
        <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-[11px] text-muted-foreground">Indicadores</span>
          <TramiteBudgetIndicators tramite={tramite} />
        </div>
      )}
    </article>
  );
};
